import { useEffect, useState } from 'react';
import { ShieldCheck, KeyRound, Loader2 } from 'lucide-react';
import { apiPanel } from './api';
import { useAuthPanel } from './contextoPanel';
import CampoContrasena from '../ui/CampoContrasena';

// Acceso al panel de plataforma. Tres campos, no dos: el segundo factor no
// es un paso opcional que se activa después, es parte del acceso.
//
// Antes de pintar nada se pregunta si el panel existe en esta instalación.
// Sin eso, una instalación de un solo cliente enseñaría un formulario que
// siempre falla con un 503 que nadie sabe interpretar.
export default function Acceso() {
  const { iniciarSesion } = useAuthPanel();
  const [habilitado, setHabilitado] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    apiPanel
      .salud()
      .then((r) => setHabilitado(r.habilitado))
      .catch(() => setHabilitado(false));
  }, []);

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await iniciarSesion(email.trim(), password, codigo.trim());
    } catch (err) {
      setError(err.message || 'No se pudo iniciar sesión.');
      setCodigo(''); // Un código TOTP no se reutiliza: se pide otro.
      setEnviando(false);
    }
  }

  if (habilitado === null) {
    return (
      <Marco>
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Comprobando…
        </div>
      </Marco>
    );
  }

  if (!habilitado) {
    return (
      <Marco>
        <h1 className="text-xl font-semibold text-white mb-2">
          El panel no está habilitado aquí
        </h1>
        <p className="text-sm text-slate-400 leading-relaxed">
          Esta instalación no tiene configurado el rol de plataforma y
          no hay panel al que entrar. Es el estado normal de una instalación
          de un solo cliente.
        </p>
        <p className="text-sm text-slate-400 leading-relaxed mt-3">
          Para habilitarlo: rellena <code className="text-slate-200">PANEL_DB_PASSWORD</code>{' '}
          en el <code className="text-slate-200">.env</code>, ejecuta{' '}
          <code className="text-slate-200">./database/03-panel-role.sh</code> y
          reinicia la API.
        </p>
        <a href="/" className="inline-block mt-6 text-sm font-semibold text-blue-400 hover:text-blue-300">
          ← Volver al sistema
        </a>
      </Marco>
    );
  }

  return (
    <Marco>
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-5 w-5 text-blue-400" />
        <span className="text-xs font-bold uppercase tracking-wider text-blue-400">
          Plataforma
        </span>
      </div>
      <h1 className="text-2xl font-semibold text-white mb-1">Panel de administración</h1>
      <p className="text-sm text-slate-400 mb-6">
        Acceso del operador del servicio. No es la entrada de las empresas.
      </p>

      <form onSubmit={enviar} className="space-y-4">
        <label className="block">
          <span className="block text-sm font-semibold text-slate-300 mb-1">Correo</span>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
        </label>

        <label className="block">
          <span className="block text-sm font-semibold text-slate-300 mb-1">Contraseña</span>
          <CampoContrasena
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            claseBoton="text-slate-300 hover:text-white"
            required
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
        </label>

        <label className="block">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-300 mb-1">
            <KeyRound className="h-3.5 w-3.5" aria-hidden="true" /> Código del autenticador
          </span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            value={codigo}
            // Peaje U-02: sin maxLength, que recortaba lo pegado ANTES de limpiarlo
            // («123 456» → «123 45»); se limpia y DESPUÉS se corta a 6.
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoComplete="one-time-code"
            required
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white font-mono text-lg tracking-[0.4em] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
          <span className="block text-sm text-slate-400 mt-1">
            Seis dígitos. Si no coincide, revisa la hora del teléfono.
          </span>
        </label>

        {error && (
          <div role="alert" className="text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg p-2.5">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={enviando || codigo.length !== 6}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-semibold py-2.5 rounded-lg"
        >
          {enviando ? 'Entrando…' : 'Entrar'}
        </button>
      </form>

      {/* Peaje U-02: slate-500 sobre slate-900 daba 3,74:1 (medido); slate-400 pasa AA. */}
      <p className="text-sm text-slate-400 mt-6 leading-relaxed">
        Esta cuenta ve el estado comercial de todos los clientes. No puede ver
        el inventario de ninguno: lo impide PostgreSQL, no esta pantalla.
      </p>
    </Marco>
  );
}

function Marco({ children }) {
  return (
    <div className="min-h-dvh bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
        {children}
      </div>
    </div>
  );
}
